import * as React from 'react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { Box, Typography, Card } from '@mui/material'
import {
  useLogin,
  useNotify,
  useSafeSetState,
  useTranslate,
  Notification,
  useQuery,
  sanitizeFetchType,
} from 'react-admin'
import DraggableComponent from '../DraggableComponent'
import { ReactComponent as Logo } from '../svg/logotype.svg'
import { ResourceName } from '../../../core/ResourceName'
import { LoginErrors } from '../../../core/enum/LoginErrors'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundImage:
        'radial-gradient(circle at 50% 14em, #ED7C00 0%, #5f5449ff 50%, #373737 100%);',
    },
    loginBox: {
      marginTop: '-40vh',
      width: 300,
    },
    loginHeader: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '1em',
    },
    formField: {
      marginBottom: theme.spacing(2),
    },
    submitButton: {
      color: '#373737',
      backgroundColor: '#ED7C00',
      '&:hover': {
        backgroundColor: '#373737',
        color: '#ED7C00',
      },
    },
    forgotPassword: {
      textDecoration: 'none',
      color: '#ED7C00',
      cursor: 'pointer',
    },
    dialogActions: {
      height: '16px',
      background: 'rgba(0,0,0,0.2)',
      minWidth: '365px',
      userSelect: 'none',
    },
    dialogTitle: {
      paddingLeft: '10px',
      width: '100%',
    },
    dialogCloseButton: {
      margin: 0,
    },
    dialogContent: {
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
    },
  }),
)

const ForgotPasswordDialog = ({ ...props }) => {
  const { open, handleClose } = props
  const classes = useStyles()
  const translate = useTranslate()
  const notify = useNotify()
  const [email, setEmail] = useState<string>('')
  const [sendEmail, setSendEmail] = useState<boolean>(false)
  const [sendEmailError, setSendEmailError] = useState<boolean>(false)

  const sendResetPasswordLinkSubmit = async () => {
    setSendEmailError(false)
    setSendEmail(true)
  }

  const isEmail = (value: string | null | undefined): boolean => {
    if (!value) return false
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(value.trim())
  }

  useQuery(
    {
      type: sanitizeFetchType('sendResetPasswordLink'),
      resource: ResourceName.CURRENT_USER,
      payload: {
        email,
      },
    },
    {
      enabled: !!email && !!sendEmail,
      onFailure: () => {
        setSendEmail(false)
        setSendEmailError(true)
        notify(
          translate('resources.auth.password-reset-link.link-not-sent'),
          'error',
          {},
          false,
          10000,
        )
      },
      onSuccess: () => {
        setSendEmail(false)
        setSendEmailError(false)
        setEmail('')
        notify(
          translate('resources.auth.password-reset-link.link-sent'),
          'info',
          {},
          false,
          10000,
        )
      },
    },
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogActions
        className={classes.dialogActions}
        style={{ cursor: 'move' }}
      >
        <Typography className={classes.dialogTitle} id="draggable-dialog-title">
          {translate('resources.auth.password-reset-link.name')}
        </Typography>
        <IconButton
          size="small"
          className={classes.dialogCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent className={classes.dialogContent}>
        <Box component="form" onSubmit={sendResetPasswordLinkSubmit}>
          <TextField
            className={classes.formField}
            variant="standard"
            label="Email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            error={sendEmailError}
            helperText={
              sendEmailError
                ? translate('resources.auth.password-reset-link.sending-error')
                : ''
            }
          />
          <Button
            className={classes.submitButton}
            variant="contained"
            type="submit"
            disabled={!email || !isEmail(email)}
            fullWidth
            onClick={sendResetPasswordLinkSubmit}
          >
            {sendEmail ? (
              <CircularProgress size={25} thickness={2} />
            ) : (
              translate('resources.auth.password-reset.send-reset-link')
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

const LoginForm = () => {
  const translate = useTranslate()
  const classes = useStyles()
  const notify = useNotify()
  const login = useLogin()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [credentials, setCredentials] = useSafeSetState({
    username: '',
    password: '',
  })
  const [openForgotPassword, setOpenForgotPassword] = useState<boolean>(false)

  const getErrorMessage = (error: any) => {
    if (typeof error === 'string') return translate(error)
    if (error?.message) {
      if (error.message.startsWith(LoginErrors.TEMPORARY_BAN)) {
        const banUntilStr = error.message.split(' ')[1]
        const banUntilDate = new Date(banUntilStr)
        const now = new Date()
        const diffMs = banUntilDate.getTime() - now.getTime()
        if (diffMs > 63072000000) {
          return translate('ra.auth.permanent_ban')
        }

        const diffMinutes = Math.floor(diffMs / (1000 * 60))
        const days = Math.floor(diffMinutes / (60 * 24))
        const hours = Math.floor((diffMinutes % (60 * 24)) / 60)
        const minutes = diffMinutes % 60

        const message = translate('ra.auth.temporary_ban')
        return `${message} ${days} ${translate(
          'ra.auth.temporary-ban.days',
        )} ${hours} ${translate(
          'ra.auth.temporary-ban.hours',
        )} ${minutes} ${translate('ra.auth.temporary-ban.minutes')}`
      }
      return translate(error.message)
    }
    return translate('ra.auth.sign_in_error')
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()
    setLoading(true)
    login(credentials).catch((error: any) => {
      setLoading(false)
      notify(getErrorMessage(error), 'warning', { _: getErrorMessage(error) })
      if (getErrorMessage(error) === LoginErrors.PASSWORD_EXPIRED)
        history.push('/password-expired')
    })
  }

  const handleChange = (e: any) =>
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    })

  const handleCloseForgotPassword = () => {
    setOpenForgotPassword(false)
  }

  return (
    <Card>
      <ForgotPasswordDialog
        open={openForgotPassword}
        handleClose={handleCloseForgotPassword}
      />
      <div className={classes.loginHeader} aria-label="SPORTDATA">
        <Logo style={{ height: 60 }} />
      </div>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          padding: '0 1em 1em 1em',
        }}
      >
        <TextField
          label={translate('ra.auth.username')}
          name="username"
          value={credentials.username}
          onChange={handleChange}
          fullWidth
          autoFocus
          margin="normal"
        />
        <TextField
          label={translate('ra.auth.password')}
          name="password"
          type="password"
          value={credentials.password}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <Typography
          className={classes.forgotPassword}
          onClick={() => setOpenForgotPassword(true)}
          variant="body2"
          sx={{ mt: 1, mb: 1, textAlign: 'right' }}
        >
          {translate('resources.auth.forgot-password')}
        </Typography>
        <Button
          className={classes.submitButton}
          variant="contained"
          type="submit"
          disabled={loading || !credentials?.username || !credentials?.password}
          fullWidth
        >
          {loading ? (
            <CircularProgress size={25} thickness={2} />
          ) : (
            translate('ra.auth.sign_in')
          )}
        </Button>
      </Box>
    </Card>
  )
}

export const LoginView = (props: any) => {
  const classes = useStyles()

  return (
    <div className={classes.loginWrapper}>
      <div className={classes.loginBox}>
        <LoginForm {...props} />
        <Notification />
      </div>
    </div>
  )
}
